/**
 * ActionItemBadge.jsx
 * 
 * A notification badge component that displays the count of pending action items
 * for the current user. Fetches counts from the backend and updates periodically.
 * 
 * Features:
 * - Shows total pending items (assigned + collaborator)
 * - Highlights overdue items with warning color
 * - Auto-refreshes every 60 seconds
 * - Compact pill badge design
 * - Failure tracking: pauses retries after 5 consecutive failures, auto-resumes after 5 minutes
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

/**
 * Action Item notification badge component.
 * Displays the count of pending action items assigned to or involving the current user.
 * Pauses polling after repeated failures and auto-resumes after a recovery timeout.
 * 
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.showZero=false] - Whether to show badge when count is 0
 * @returns {JSX.Element|null} Badge element or null if no items
 */
const ActionItemBadge = ({ className = '', showZero = false }) => {
    const { apiFetch, user } = useAuth();
    const [badgeCounts, setBadgeCounts] = useState({
        assignedCount: 0,
        collaboratorCount: 0,
        overdueCount: 0,
        recentActivityCount: 0,
        totalPending: 0
    });
    const [loading, setLoading] = useState(true);

    // Track consecutive failures — pauses polling after MAX_CONSECUTIVE_FAILURES
    const consecutiveFailuresRef = useRef(0);
    // Maximum number of consecutive failures before pausing retries
    const MAX_CONSECUTIVE_FAILURES = 5;
    // Recovery timeout (5 minutes) — after pausing due to failures, resume retrying
    const RECOVERY_TIMEOUT_MS = 300000;
    // Ref to hold the recovery timer so it can be cleaned up on unmount
    const recoveryTimerRef = useRef(null);

    /**
     * Fetches badge counts from the backend API.
     * After MAX_CONSECUTIVE_FAILURES consecutive failures, pauses polling
     * and schedules a recovery timer to resume after RECOVERY_TIMEOUT_MS.
     */
    const fetchBadgeCounts = useCallback(async () => {
        if (!user?.userID) return;

        // If we've exceeded the max consecutive failures, stop trying
        // (the recovery timer will reset the counter to resume)
        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) return;
        
        try {
            const response = await apiFetch(`/api/ActionItems/action-items/badge-counts/${user.userID}`);
            const data = await response.json();
            
            if (data.success && data.data) {
                setBadgeCounts(data.data);
                // Reset failure counter on success
                consecutiveFailuresRef.current = 0;
            } else {
                consecutiveFailuresRef.current += 1;
                if (consecutiveFailuresRef.current === 1) {
                    console.warn('Badge counts API returned unsuccessful response');
                }
                if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                    console.warn(`Action item badge paused after ${MAX_CONSECUTIVE_FAILURES} consecutive failures. Will resume in ${RECOVERY_TIMEOUT_MS / 60000} minutes.`);
                    // Schedule automatic recovery after the timeout period
                    recoveryTimerRef.current = setTimeout(() => { consecutiveFailuresRef.current = 0; }, RECOVERY_TIMEOUT_MS);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            consecutiveFailuresRef.current += 1;
            // Only log on first failure to avoid spamming the console
            if (consecutiveFailuresRef.current === 1) {
                console.error('Error fetching action item badge counts:', error);
            }
            if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                console.warn(`Action item badge paused after ${MAX_CONSECUTIVE_FAILURES} consecutive failures. Will resume in ${RECOVERY_TIMEOUT_MS / 60000} minutes.`);
                // Schedule automatic recovery after the timeout period
                recoveryTimerRef.current = setTimeout(() => { consecutiveFailuresRef.current = 0; }, RECOVERY_TIMEOUT_MS);
            }
        } finally {
            setLoading(false);
        }
    }, [user, apiFetch]);

    // Configurable refresh interval (in milliseconds)
    // Can be adjusted based on server load and user needs
    const BADGE_REFRESH_INTERVAL_MS = 60000; // 60 seconds

    // Initial fetch and periodic refresh
    useEffect(() => {
        (async () => { await fetchBadgeCounts(); })();

        // Set up periodic refresh
        const intervalId = setInterval(fetchBadgeCounts, BADGE_REFRESH_INTERVAL_MS);
        
        return () => {
            clearInterval(intervalId);
            // Clear the recovery timer if the component unmounts to avoid leaked timers
            if (recoveryTimerRef.current) {
                clearTimeout(recoveryTimerRef.current);
                recoveryTimerRef.current = null;
            }
        };
    }, [fetchBadgeCounts]);

    // Don't render if no items and showZero is false
    if (!loading && badgeCounts.totalPending === 0 && !showZero) {
        return null;
    }

    // Determine badge styling based on overdue count
    const hasOverdue = badgeCounts.overdueCount > 0;
    const badgeStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '20px',
        height: '20px',
        padding: '0 6px',
        borderRadius: '10px',
        fontSize: '0.75rem',
        fontWeight: '600',
        color: '#fff',
        backgroundColor: hasOverdue ? '#F63040' : '#57565B',
        marginLeft: '8px',
        lineHeight: '1'
    };

    return (
        <span 
            className={`action-item-badge ${className}`} 
            style={badgeStyle}
            title={`${badgeCounts.assignedCount} assigned, ${badgeCounts.collaboratorCount} as collaborator, ${badgeCounts.overdueCount} overdue`}
        >
            {loading ? '...' : badgeCounts.totalPending}
        </span>
    );
};

export default ActionItemBadge;
